// Option C (2026-10-11): the shared momentum-fund signal panel's contract.
// GoldSignalPanel.vue and GgeSignalPanel.vue were copy-pasted siblings; they
// are now one FundSignalPanel.vue driven by a FundSignalPanelConfig per fund,
// declared on the fund page registry (FUND_PAGES[slug].signalPanel). Every
// per-fund difference lives in the config — the panel has no fund forks.
//
// Kept component-free (types + pure helpers) so the panel, the configs and
// the explainers can all import it without a cycle.
import type { Component } from 'vue'
import type { FundNavLatest, GoldKtLatestResponse } from '../../api/client'
import type { LiveSynthNavHandle } from '../../composables/useLiveSynthNav'
import type { GoldOverlayView } from '../../utils/goldOverlay'

/** Live-preview fields the panel reads (structural over Gold/GgeSignalPreview). */
export interface FundSignalPreviewLike {
  decision?: 'BUY' | 'SELL' | 'HOLD'
  m_stale?: boolean
  degraded?: boolean
  last_bar_age_min?: number | null
  threshold_bps?: number | null
  timestamp?: string
  gate_open?: boolean
}

/** Stamped-decision fields the panel reads (structural over Gold/GgeSignalResponse). */
export interface FundSignalPayloadLike {
  available?: boolean
  decision?: 'BUY' | 'SELL' | 'HOLD'
  position_proxy?: 'BUY' | 'SELL' | null
  degraded?: boolean
  reason?: string
  m_reason?: string | null
  threshold_bps?: number | null
  threshold_reason?: string
  timestamp?: string
  preview?: FundSignalPreviewLike | null
}

/** The live synth-NAV hero's fields (shared by GoldSynthNavLive / GgeSynthNavLive). */
export interface LiveSynthNavLike {
  synthNavPence: number
  anchorPence: number
  liveReturnGbp: number
  tier: 'live' | 'degraded' | 'critical' | 'closed'
  coveredWeightFrac: number
}

/** Everything the panel has computed, handed to the config's stats and the explainer. */
export interface FundSignalView {
  signal: FundSignalPayloadLike | null
  mBps: number | null
  thresholdBps: number | null
  thresholdReasonText: string | null
  decisionLabel: string
  decisionReasonText: string | null
  ktLatest: GoldKtLatestResponse | null
  /** null when the fund has no live overlay bridge. */
  overlay: GoldOverlayView | null
}

/** One cell of the stats grid between the momentum and "Decision at" cells. */
export interface FundSignalStat {
  label: string
  /** Rendered as <sub> after the label (e.g. Δy<sub>20</sub>). */
  labelSub?: string
  /** (i) button opening explainer section `key`. */
  info?: { key: string; testid: string; aria: string }
  value: string
  valueTestId?: string
  valueClass?: string
  sub: string
}

export interface FundSignalPanelConfig {
  /** data-testid / data-test prefix, e.g. 'gold' → gold-decision, gold-hero-nav. */
  key: string
  title: string
  /** Legacy root class kept alongside .fund-signal (gold-signal / gge-signal). */
  rootClass: string
  /** "<errorName> signal unavailable — API error." */
  errorName: string
  /** "Awaiting today's <awaitName> decision …" */
  awaitName: string
  redisKey: string
  /** Stat values, cutoff countdown and preview m colour. */
  accent: string
  /** Explainer left border (behind var(--accent, …)). */
  explainerAccent: string
  fetchSignal: () => Promise<FundSignalPayloadLike>
  /** Decimal-fraction momentum field on the payload and its preview (m_1040 / m_0840). */
  momentumField: string
  /** Decision-time subscript on the m stat: '10:40' / '08:40'. */
  momentumSub: string
  /** Explainer section key of the m stat: 'm1040' / 'm0840'. */
  momentumExplainerKey: string
  /** Futures symbol in the preview strip ("GC bar 3m old", "no fresh CL print"). */
  futuresSymbol: string
  /** Preview strip shows the would-be gate state (gold's yield gate). */
  previewShowsGate: boolean
  /** Fund key into CalendarStatus.fundClosures / nextDealingDay; absent = UK trading calendar. */
  dealingCalendarKey?: string
  fetchKtLatest: () => Promise<GoldKtLatestResponse>
  /** Optional official-NAV leg in the telemetry (GGE): adds "NAV …p ·" before K_t. */
  fetchNavLatest?: () => Promise<FundNavLatest>
  /** data-testid suffix of the telemetry figure: 'kt-figure' / 'nav-figure'. */
  telemetryTestIdSuffix: string
  useLiveSynthNav: () => LiveSynthNavHandle<LiveSynthNavLike>
  strikeAnchorFund: 'gold' | 'gge'
  /** Live overlay bridge under the telemetry (gold only). */
  overlay?: { derive: (kt: GoldKtLatestResponse | null) => GoldOverlayView; assetLabel: string }
  /** m_reason → explainer pill beside the degraded badge (GGE crude-roll guard). */
  degradedPills?: Record<string, { text: string; title: string; testid: string }>
  /** reason code → English for the reason row and decision explainer. */
  decisionReasons: Record<string, string>
  /** The two middle cells of the stats grid. */
  extraStats: (v: FundSignalView) => FundSignalStat[]
  /** Fund-specific explainer prose; props { section: string; v: FundSignalView }. */
  explainer: Component
  /** Cards rendered after HoldingsPanel on the Signal tab (GGE swing monitor). */
  afterHoldings?: Component[]
}

export function bps(x: number | null | undefined, digits = 1): string {
  if (x == null || Number.isNaN(x)) return '—'
  const sign = x > 0 ? '+' : ''
  return `${sign}${x.toFixed(digits)} bps`
}

export function fmtTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-GB', {
      timeZone: 'Europe/London',
      hour: '2-digit', minute: '2-digit',
      day: '2-digit', month: 'short',
    })
  } catch {
    return iso
  }
}

export function fmtUsd(x: number | null | undefined): string {
  if (x == null || Number.isNaN(x)) return '—'
  return '$' + x.toFixed(2)
}
